import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';

import { loginState } from 'global-stores';
import { API_URL } from 'constant';
import { useNetwork } from 'hooks/network';
import { signInToFirebase } from './firebase';

/**
 * Bootstraps Firebase authentication for the admin panel.
 *
 * Once the user is logged in (a platform access token exists), this fetches a
 * Firebase custom token from the backend (`POST /firebase-token`) and signs in
 * to Firebase. RTDB reads/writes now require an authenticated user, so this
 * should be mounted once at the authenticated app root, before any view that
 * uses the Realtime Database renders.
 */
export const useFirebaseAuth = () => {
	const { accessToken } = useRecoilValue(loginState);
	const { post } = useNetwork();
	const requestedRef = useRef(false);

	useEffect(() => {
		if (!accessToken || requestedRef.current) {
			return;
		}
		requestedRef.current = true;

		(async () => {
			try {
				const resp = await post(API_URL.FIREBASE_TOKEN, {});
				const firebaseToken =
					resp?.data?.firebaseToken ??
					resp?.firebaseToken ??
					resp?.data?.data?.firebaseToken;
				await signInToFirebase(firebaseToken);
			} catch (error) {
				// Allow a retry on a later render if the fetch failed.
				requestedRef.current = false;
				// eslint-disable-next-line no-console
				console.error('Failed to obtain Firebase token', error);
			}
		})();
	}, [accessToken, post]);
};
